export type SupportedLocale = "en" | "es" | "zh" | "ja" | "ru" | "de" | "fr" | "pt";

export interface LocaleMeta {
  name: string;
  native: string;
  flag: string;
}

export const LOCALE_META: Record<SupportedLocale, LocaleMeta> = {
  en: { name: "English", native: "English", flag: "🇺🇸" },
  es: { name: "Spanish", native: "Español", flag: "🇪🇸" },
  zh: { name: "Chinese Simplified", native: "中文", flag: "🇨🇳" },
  ja: { name: "Japanese", native: "日本語", flag: "🇯🇵" },
  ru: { name: "Russian", native: "Русский", flag: "🇷🇺" },
  de: { name: "German", native: "Deutsch", flag: "🇩🇪" },
  fr: { name: "French", native: "Français", flag: "🇫🇷" },
  pt: { name: "Portuguese", native: "Português", flag: "🇧🇷" },
};

const COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  US: "en",
  GB: "en",
  AU: "en",
  NZ: "en",
  IE: "en",
  ES: "es",
  MX: "es",
  AR: "es",
  CL: "es",
  CO: "es",
  PE: "es",
  VE: "es",
  UY: "es",
  PY: "es",
  CN: "zh",
  HK: "zh",
  TW: "zh",
  SG: "zh",
  JP: "ja",
  RU: "ru",
  DE: "de",
  AT: "de",
  CH: "de",
  FR: "fr",
  BE: "fr",
  CA: "fr",
  PT: "pt",
  BR: "pt",
  AO: "pt",
  MZ: "pt",
};

/**
 * Detect the browser locale and match it with our supported locales.
 * Uses the browser language plus region/country hints so users in Spanish/Portuguese/German/etc.
 * locales get the right app language automatically.
 */
export function getBrowserLocale(): SupportedLocale {
  if (typeof navigator === "undefined") return "en";

  const candidates = [navigator.language, ...(navigator.languages || []), Intl.DateTimeFormat().resolvedOptions().locale]
    .filter((value): value is string => Boolean(value));

  for (const candidate of candidates) {
    const normalized = candidate.replace(/_/g, "-");
    const [language, region] = normalized.split("-");
    const base = language?.toLowerCase();
    if (base && base in LOCALE_META) return base as SupportedLocale;

    if (region) {
      const country = region.toUpperCase();
      const mapped = COUNTRY_TO_LOCALE[country];
      if (mapped) return mapped;
    }
  }

  return "en";
}
